/**
 * SettlementPage Component (Optimized)
 * 앱웹 전용 정산 페이지 - VROONG 브랜드 통일 디자인
 */

import React, { useRef, useMemo, useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNavigation from '../../../core/components/BottomNavigation';
import CustomSelect, { SelectOption } from '../../../core/components/CustomSelect';
import { useSettlement } from '../hooks/useSettlement';
import { useDeferredLoading } from '../../../core/hooks/useDeferredLoading';
import { getUserInfo } from '../../../core/utils';
import styles from '../styles/SettlementPage.module.css';
import { useMaintenanceContext } from '../../system/maintenance/contexts/MaintenanceContext';

// ============================================================================
// Sub Components
// ============================================================================

/**
 * 정산 상세 행 컴포넌트
 */
interface DetailRowProps {
    label: string;
    value: string | number | null | undefined;
    highlight?: boolean;
    negative?: boolean;
    isCompanyName?: boolean;
    unit?: string; // 단위 추가 (기본값: '원')
}

const SettlementDetailRow: React.FC<DetailRowProps> = React.memo(({ 
    label, 
    value, 
    highlight, 
    negative,
    isCompanyName,
    unit = '원' // 기본값은 '원'
}) => {
    let formattedValue: string;
    
    if (typeof value === 'number') {
        formattedValue = `${value.toLocaleString()}${unit}`;
    } else if (value === null || value === undefined) {
        formattedValue = `0${unit}`;
    } else {
        formattedValue = value;
    }
    
    return (
        <div className={`${styles['detail-row']} ${isCompanyName ? styles['company-name'] : ''}`}>
            <span className={styles['detail-label']}>{label}</span>
            <span className={`${styles['detail-value']} ${highlight ? styles['highlight'] : ''} ${negative ? styles['negative'] : ''}`}>
                {formattedValue}
            </span>
        </div>
    );
});

SettlementDetailRow.displayName = 'SettlementDetailRow';

/**
 * 금액 카드 컴포넌트
 */
interface AmountCardProps {
    type: 'withdrawal' | 'settlement';
    label: string;
    amount: number | null | undefined;
    icon: string;
}

const SettlementAmountCard: React.FC<AmountCardProps> = React.memo(({ type, label, amount, icon }) => {
    const displayAmount = amount ?? 0;
    
    return (
        <div className={`${styles['amount-card']} ${styles[type]}`}>
            <div className={styles['amount-icon']}>{icon}</div>
            <div className={styles['amount-label']}>{label}</div>
            <div className={styles['amount-value']}>
                {displayAmount.toLocaleString()}
                <span className={styles['amount-unit']}>원</span>
            </div>
        </div>
    );
});

SettlementAmountCard.displayName = 'SettlementAmountCard';

/**
 * 미수금 배너 컴포넌트
 */
interface UnpaidAmountBannerProps {
    unpaidAmt: number;
}

const UnpaidAmountBanner: React.FC<UnpaidAmountBannerProps> = React.memo(({ unpaidAmt }) => {
    if (!unpaidAmt || unpaidAmt >= 0) return null;
    const displayAmt = Math.abs(unpaidAmt);

    return (
        <div className={styles['unpaid-banner']}>
            <div className={styles['unpaid-icon']}>⚠️</div>
            <div className={styles['unpaid-content']}>
                <span className={styles['unpaid-label']}>미수금</span>
                <span className={styles['unpaid-amount']}>{displayAmt.toLocaleString()}원</span>
            </div>
        </div>
    );
});

UnpaidAmountBanner.displayName = 'UnpaidAmountBanner';

/**
 * 라이더 정산 상세 컴포넌트
 */
interface RiderSettlementDetailsProps {
    stats: {
        totalSettlementAmount: number;
        deliveryProcessFee: number;
        additionalPayment: number;
        completionCount: number;
        promotion: number;
        operationFee: number;
        advanceDeduction: number;
        hdcDeduction: number;
        sdcDeduction: number;
        recoveryAmt: number;
        recoverAmt: number;
        employmentInsurance: number;
        industrialAccidentInsurance: number;
        hourlyInsurance: number;
        withholdingTax: number;
        advancePayment: number;
    };
}

const RiderSettlementDetails: React.FC<RiderSettlementDetailsProps> = React.memo(({ stats }) => {
    const [isDeductExpanded, setIsDeductExpanded] = useState(false);
    const toggleDeduct = useCallback(() => setIsDeductExpanded(prev => !prev), []);

    return (
        <div className={styles['details-section']}>
            <h2 className={styles['section-title']}>
                <span className={styles['section-title-icon']}>📋</span>
                정산 상세
            </h2>
            <div className={styles['details-list']}>
                <SettlementDetailRow label="배달처리비" value={stats.deliveryProcessFee} />
                <SettlementDetailRow label="추가지급" value={stats.additionalPayment} />
                <SettlementDetailRow label="완료건수" value={stats.completionCount} highlight unit="건" />
                <SettlementDetailRow label="프로모션" value={stats.promotion} highlight />

                <div className={styles['section-divider']}></div>

                <SettlementDetailRow label="운영비" value={stats.operationFee} />
                <SettlementDetailRow label="본사 일차감비" value={stats.hdcDeduction} negative />

                {/* 협력사 일차감비 + 미수금/회수금 토글 */}
                <div className={styles['expandable-section']}>
                    <div className={styles['detail-row']}>
                        <span className={styles['detail-label']}>
                            협력사 일차감비
                            <button
                                className={styles['expand-btn']}
                                onClick={toggleDeduct}
                                aria-expanded={isDeductExpanded}
                                aria-label="미수금·회수금 상세 보기"
                            >
                                {isDeductExpanded ? '−' : '+'}
                            </button>
                        </span>
                        <span className={`${styles['detail-value']} ${styles['negative']}`}>
                            {(stats.sdcDeduction ?? 0).toLocaleString()}원
                        </span>
                    </div>
                    {isDeductExpanded && (
                        <div className={styles['sub-rows']}>
                            <div className={styles['sub-row']}>
                                <span className={styles['sub-label']}>당일 일차감</span>
                                <span className={styles['sub-value']}>
                                    {((stats.sdcDeduction ?? 0) - (stats.recoverAmt ?? 0)).toLocaleString()}원
                                </span>
                            </div>
                            <div className={styles['sub-row']}>
                                <span className={styles['sub-label']}>미수금</span>
                                <span className={styles['sub-value']}>
                                    {(stats.recoveryAmt ?? 0).toLocaleString()}원
                                </span>
                            </div>
                            <div className={styles['sub-row']}>
                                <span className={styles['sub-label']}>회수금</span>
                                <span className={styles['sub-value']}>
                                    {(stats.recoverAmt ?? 0).toLocaleString()}원
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles['section-divider']}></div>

                <SettlementDetailRow label="고용보험" value={stats.employmentInsurance} />
                <SettlementDetailRow label="산재보험" value={stats.industrialAccidentInsurance} />
                <SettlementDetailRow label="시간제보험" value={stats.hourlyInsurance} />

                <div className={styles['section-divider']}></div>

                <SettlementDetailRow label="원천세" value={stats.withholdingTax} />
                <SettlementDetailRow label="사전공제" value={0} />
                <SettlementDetailRow label="선지급배달료" value={stats.advancePayment} />
                <SettlementDetailRow
                    label="실지급액"
                    value={stats.totalSettlementAmount}
                    highlight
                />
            </div>
        </div>
    );
});

RiderSettlementDetails.displayName = 'RiderSettlementDetails';

/**
 * 협력사 정산 상세 컴포넌트
 */
interface SupplierSettlementDetailsProps {
    stats: {
        bizName?: string;
        deliveryVal?: number;
        procCnt?: number;
        addAcctVal?: number;
        maintncVal?: number;
        supplyAmt?: number;
        taxVat?: number;
        supplyPrice?: number;
        manageFee?: number;
        achievementPayback?: number;
        promotion?: number;
        paymentMgmtFee?: number;
        deductVal?: number;
        suppEmpVal?: number;
        suppAccidtVal?: number;
        totInsureVal?: number;
    } | null;
}

const SupplierSettlementDetails: React.FC<SupplierSettlementDetailsProps> = React.memo(({ stats }) => {
    if (!stats) return null;
    
    return (
        <div className={styles['details-section']}>
            <h2 className={styles['section-title']}>
                <span className={styles['section-title-icon']}>📋</span>
                정산 상세
            </h2>
            <div className={styles['details-list']}>
                <SettlementDetailRow label="협력사명" value={stats.bizName || '-'} isCompanyName />
                
                <SettlementDetailRow label="배달료" value={stats.deliveryVal || 0} />
                <SettlementDetailRow label="처리건수" value={stats.procCnt || 0} highlight unit="건" />
                <SettlementDetailRow label="추가정산" value={stats.addAcctVal || 0} />
                
                <div className={styles['section-divider']}></div>
                
                <SettlementDetailRow label="관리비" value={stats.maintncVal || 0} />
                <SettlementDetailRow label="공급가액" value={stats.supplyAmt || 0} />
                <SettlementDetailRow label="부가세액" value={stats.taxVat || 0} />
                <SettlementDetailRow label="공급대가" value={stats.supplyPrice || 0} />
                
                <div className={styles['section-divider']}></div>
                
                <SettlementDetailRow label="지점 운영 수수료" value={stats.manageFee || 0} />
                <SettlementDetailRow label="달성 페이백" value={stats.achievementPayback || 0} highlight />
                <SettlementDetailRow label="프로모션" value={stats.promotion || 0} />
                <SettlementDetailRow label="지급관리비(VAT포함)" value={stats.paymentMgmtFee || 0} />
                
                <div className={styles['section-divider']}></div>
                
                <SettlementDetailRow label="일차감" value={stats.deductVal || 0} negative />
                <SettlementDetailRow label="사업주_고용" value={stats.suppEmpVal || 0} />
                <SettlementDetailRow label="사업주_산재" value={stats.suppAccidtVal || 0} />
                <SettlementDetailRow label="보험료합계" value={stats.totInsureVal || 0} />
            </div>
        </div>
    );
});

SupplierSettlementDetails.displayName = 'SupplierSettlementDetails';

/**
 * 정산 타입 탭 컴포넌트
 */
interface SettlementTabsProps {
    isSupplier: boolean;
    settlementType: string;
    onSettlementTypeChange: (type: 'weekly' | 'pre') => void;
}

const SettlementTabs: React.FC<SettlementTabsProps> = React.memo(({ 
    isSupplier, 
    settlementType, 
    onSettlementTypeChange 
}) => {
    const handleWeeklyClick = useCallback(() => onSettlementTypeChange('weekly'), [onSettlementTypeChange]);
    const handlePreClick = useCallback(() => onSettlementTypeChange('pre'), [onSettlementTypeChange]);

    if (isSupplier) {
        return (
            <div className={styles['settlement-tabs']}>
                <button className={`${styles['tab-btn']} ${styles['active']}`}>
                    주정산
                </button>
            </div>
        );
    }

    return (
        <div className={styles['settlement-tabs']}>
            <button
                className={`${styles['tab-btn']} ${settlementType === 'weekly' ? styles['active'] : ''}`}
                onClick={handleWeeklyClick}
            >
                주정산
            </button>
            <button
                className={`${styles['tab-btn']} ${settlementType === 'pre' ? styles['active'] : ''}`}
                onClick={handlePreClick}
            >
                선정산
            </button>
        </div>
    );
});

SettlementTabs.displayName = 'SettlementTabs';

/**
 * 날짜 선택 컴포넌트
 */
interface DateRangeSelectorProps {
    isSupplier: boolean;
    weekSelectOptions: SelectOption[];
    selectedWeek: string;
    onWeekChange: (value: string) => void;
    riderWeekSelectOptions: SelectOption[];
    selectedRiderWeek: string;
    onRiderWeekChange: (value: string) => void;
    fromDate: string;
    toDate: string;
    onFromDateChange: (value: string) => void;
    onToDateChange: (value: string) => void;
    isWeeklySettlement: boolean;
    getWednesdayOnlyDateRange: () => { min: string; max: string };
    fromDateRef: React.RefObject<HTMLInputElement | null>;
    toDateRef: React.RefObject<HTMLInputElement | null>;
    onFromDateIconClick: () => void;
    onToDateIconClick: () => void;
    settlementType: string;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = React.memo(({
    isSupplier,
    weekSelectOptions,
    selectedWeek,
    onWeekChange,
    riderWeekSelectOptions,
    selectedRiderWeek,
    onRiderWeekChange,
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
    isWeeklySettlement,
    getWednesdayOnlyDateRange,
    fromDateRef,
    toDateRef,
    onFromDateIconClick,
    onToDateIconClick,
    settlementType,
}) => {
    const handleFromDateChangeWrapper = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        onFromDateChange(e.target.value);
    }, [onFromDateChange]);

    const handleToDateChangeWrapper = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        onToDateChange(e.target.value);
    }, [onToDateChange]);

    const preventKeyDown = useCallback((e: React.KeyboardEvent) => {
        e.preventDefault();
    }, []);

    const handleSingleDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        onFromDateChange(e.target.value);
        onToDateChange(e.target.value);
    }, [onFromDateChange, onToDateChange]);

    if (isSupplier) {
        return (
            <div className={styles['selector-section']}>
                <CustomSelect
                    options={weekSelectOptions}
                    value={selectedWeek}
                    onChange={onWeekChange}
                    placeholder="주차 선택"
                />
            </div>
        );
    }

    // 라이더: 주정산일 때는 주차별 선택, 선정산일 때는 날짜 범위 선택
    if (settlementType === 'weekly') {
        return (
            <div className={styles['selector-section']}>
                <CustomSelect
                    options={riderWeekSelectOptions}
                    value={selectedRiderWeek}
                    onChange={onRiderWeekChange}
                    placeholder="주차 선택"
                />
            </div>
        );
    }

    // 선정산: 단일 날짜 선택
    return (
        <div className={styles['selector-section']}>
            <div className={styles['date-input-box']}>
                <input
                    ref={fromDateRef}
                    type="date"
                    className={styles['date-input']}
                    value={fromDate}
                    onChange={handleSingleDateChange}
                    onKeyDown={preventKeyDown}
                />
                <i className={`fas fa-calendar-alt ${styles['date-icon']}`} onClick={onFromDateIconClick}></i>
            </div>
        </div>
    );
});

DateRangeSelector.displayName = 'DateRangeSelector';

/**
 * 액션 버튼 컴포넌트
 */
interface ActionButtonsProps {
    onTransfer: () => void;
    onWithdrawal: () => void;
}

const ActionButtons: React.FC<ActionButtonsProps> = React.memo(({ onTransfer, onWithdrawal }) => (
    <div className={styles['action-buttons']}>
        <button
            className={`${styles['action-btn']} ${styles['transfer']}`}
            onClick={onTransfer}
        >
            <i className={`fas fa-exchange-alt ${styles['action-btn-icon']}`}></i>
            송금하기
        </button>
        <button
            className={`${styles['action-btn']} ${styles['withdrawal']}`}
            onClick={onWithdrawal}
        >
            <i className={`fas fa-minus-circle ${styles['action-btn-icon']}`}></i>
            출금하기
        </button>
    </div>
));

ActionButtons.displayName = 'ActionButtons';

/**
 * 로딩 화면 컴포넌트
 */
const LoadingScreen: React.FC = React.memo(() => (
    <div className={`screen active ${styles['settlement-screen']}`}>
        <div className={styles['settlement-header']}>
            <div className={styles['header-top']}>
                <h1 className={styles['page-title']}>정산</h1>
            </div>
        </div>
        <div className={styles['settlement-container']}>
            <div className={styles['loading-section']}>
                <div className={styles['loading-spinner']}></div>
                <p className={styles['loading-text']}>정산 정보를 불러오는 중...</p>
            </div>
        </div>
        <BottomNavigation currentScreen="settlement" />
    </div>
));

LoadingScreen.displayName = 'LoadingScreen';

// ============================================================================
// Main Component
// ============================================================================

const SettlementPage: React.FC = () => {
    const navigate = useNavigate();
    const { isScheduledMaintenance } = useMaintenanceContext();
    const userInfo = useMemo(() => getUserInfo(), []);
    const isSupplier = useMemo(() => !!userInfo?.suppId, [userInfo]);

    // 정산 데이터 관리
    const {
        settlementStats,
        supplierStats,
        fromDate,
        toDate,
        settlementType,
        isLoading: rawIsLoading,
        handleFromDateChange,
        handleToDateChange,
        handleSettlementTypeChange,
        isWeeklySettlement,
        getWednesdayOnlyDateRange,
        weekOptions,
        selectedWeek,
        handleWeekChange,
        riderWeekOptions,
        selectedRiderWeek,
        handleRiderWeekChange,
    } = useSettlement();

    const isLoading = useDeferredLoading(rawIsLoading, 200);

    // Refs
    const fromDateRef = useRef<HTMLInputElement>(null);
    const toDateRef = useRef<HTMLInputElement>(null);

    // Memoized values
    const weekSelectOptions: SelectOption[] = useMemo(() => 
        weekOptions.map(opt => ({
            value: opt.value,
            label: opt.label,
        })), 
    [weekOptions]);

    const riderWeekSelectOptions: SelectOption[] = useMemo(() => 
        riderWeekOptions.map(opt => ({
            value: opt.value,
            label: opt.label,
        })), 
    [riderWeekOptions]);

    const settlementAmount = useMemo(() => {
        if (isSupplier) return supplierStats?.acctVal || 0;
        return settlementStats.totalSettlementAmount
            - (settlementStats.hdcDeduction ?? 0)
            - (settlementStats.sdcDeduction ?? 0);
    }, [isSupplier, supplierStats?.acctVal, settlementStats.totalSettlementAmount, settlementStats.hdcDeduction, settlementStats.sdcDeduction]);

    const settlementLabel = useMemo(() => 
        isSupplier ? '실 지급 금액' : '정산금액',
    [isSupplier]);

    const isCurrentlyWeekly = useMemo(() => isWeeklySettlement(), [isWeeklySettlement]);

    // Callbacks
    const handleFromDateChangeWithRestriction = useCallback((value: string) => {
        if (isWeeklySettlement()) {
            const selectedDate = new Date(value);
            if (selectedDate.getDay() !== 3) return;
        }
        handleFromDateChange(value);
    }, [isWeeklySettlement, handleFromDateChange]);

    const handleFromDateIconClick = useCallback(() => {
        fromDateRef.current?.focus();
        fromDateRef.current?.showPicker?.();
    }, []);

    const handleToDateIconClick = useCallback(() => {
        toDateRef.current?.focus();
        toDateRef.current?.showPicker?.();
    }, []);

    const [maintenanceError, setMaintenanceError] = useState<string | null>(null);

    useEffect(() => {
        if (!isScheduledMaintenance) setMaintenanceError(null);
    }, [isScheduledMaintenance]);

    const handleNavigateToTransfer = useCallback(() => {
        if (isScheduledMaintenance) {
            setMaintenanceError('잔액 정산 처리 중 (23:50 ~ 00:05)입니다. 이 시간대에는 송금과 출금이 제한됩니다.');
            return;
        }
        setMaintenanceError(null);
        navigate('/transfer');
    }, [isScheduledMaintenance, navigate]);

    const handleNavigateToWithdrawal = useCallback(() => {
        if (isScheduledMaintenance) {
            setMaintenanceError('잔액 정산 처리 중 (23:50 ~ 00:05)입니다. 이 시간대에는 송금과 출금이 제한됩니다.');
            return;
        }
        setMaintenanceError(null);
        navigate('/withdrawal');
    }, [isScheduledMaintenance, navigate]);

    // 로딩 상태
    if (isLoading) {
        return <LoadingScreen />;
    }

    return (
        <div className={`screen active ${styles['settlement-screen']}`}>
            {/* 헤더 */}
            <div className={styles['settlement-header']}>
                <div className={styles['header-top']}>
                    <h1 className={styles['page-title']}>정산</h1>
                </div>
            </div>

            {/* 메인 컨텐츠 */}
            <div className={styles['settlement-container']}>
                {/* 탭 + 날짜 선택 필터 카드 */}
                <div className={styles['filter-card']}>
                    <SettlementTabs
                        isSupplier={isSupplier}
                        settlementType={settlementType}
                        onSettlementTypeChange={handleSettlementTypeChange}
                    />
                    <DateRangeSelector
                        isSupplier={isSupplier}
                        weekSelectOptions={weekSelectOptions}
                        selectedWeek={selectedWeek}
                        onWeekChange={handleWeekChange}
                        riderWeekSelectOptions={riderWeekSelectOptions}
                        selectedRiderWeek={selectedRiderWeek}
                        onRiderWeekChange={handleRiderWeekChange}
                        fromDate={fromDate}
                        toDate={toDate}
                        onFromDateChange={handleFromDateChangeWithRestriction}
                        onToDateChange={handleToDateChange}
                        isWeeklySettlement={isCurrentlyWeekly}
                        getWednesdayOnlyDateRange={getWednesdayOnlyDateRange}
                        fromDateRef={fromDateRef}
                        toDateRef={toDateRef}
                        onFromDateIconClick={handleFromDateIconClick}
                        onToDateIconClick={handleToDateIconClick}
                        settlementType={settlementType}
                    />
                </div>

                {/* 금액 하이라이트 카드 */}
                <div className={styles['amount-highlight-section']}>
                    <div className={styles['amount-cards']}>
                        <SettlementAmountCard
                            type="withdrawal"
                            label="보유잔액"
                            amount={settlementStats.withdrawalBalance}
                            icon="💳"
                        />
                        <SettlementAmountCard
                            type="settlement"
                            label={settlementLabel}
                            amount={settlementAmount}
                            icon="💰"
                        />
                    </div>
                </div>

                {/* 미수금 배너 */}
                {!isSupplier && (
                    <UnpaidAmountBanner unpaidAmt={settlementStats.currentUnpaidAmt} />
                )}

                {/* 정산 상세 */}
                {isSupplier ? (
                    <SupplierSettlementDetails stats={supplierStats} />
                ) : (
                    <RiderSettlementDetails stats={settlementStats} />
                )}

                {/* 정기 점검 안내 */}
                {maintenanceError && (
                    <div className={styles['maintenance-notice']}>
                        <span className={styles['maintenance-notice-icon']}>⏰</span>
                        <span className={styles['maintenance-notice-text']}>{maintenanceError}</span>
                    </div>
                )}

                {/* 액션 버튼 */}
                <ActionButtons
                    onTransfer={handleNavigateToTransfer}
                    onWithdrawal={handleNavigateToWithdrawal}
                />
            </div>

            {/* 하단 네비게이션 */}
            <BottomNavigation currentScreen="settlement" />
        </div>
    );
};

export default SettlementPage;
